// High-level command FSM per squad (plan §19). Sets moveTarget for the movement system
// and engagement policy consumed by combat.

import { TUNING } from '../../content/tuning';
import { unitDef } from '../../content/registry';
import { dist, norm, sub, vec, type Vec2 } from '../../shared/vec';
import type { Sim } from '../sim';
import type { Squad } from '../state';
import { physicalLineClear } from '../mobility';
import {
  buildingAccessPoint,
  buildingApproachPoint,
  distanceToTarget,
  isHostileTarget,
  targetPosition,
} from '../targeting';

/** How far a squad may chase from its command anchor. */
export function chaseLeash(s: Squad): number {
  switch (s.command.kind) {
    case 'capture':
      return TUNING.captureRadius + 2;
    case 'defend':
      return (s.command.siteId ? TUNING.defendRadius : 10) + 8;
    case 'patrol':
      return 10;
    case 'escort':
      return 14;
    case 'raid':
      return 25;
    default:
      return Infinity;
  }
}

export function commandAnchor(sim: Sim, s: Squad): Vec2 | null {
  switch (s.command.kind) {
    case 'idle':
      return s.command.pointA ?? s.pos;
    case 'defend':
      return s.command.pointA ?? s.pos;
    case 'capture': {
      const site = sim.state.sites.find((x) => x.id === s.command.siteId);
      return site ? site.pos : s.pos;
    }
    case 'patrol':
      return s.command.patrolLegToB ? (s.command.pointB ?? s.pos) : (s.command.pointA ?? s.pos);
    case 'escort': {
      const boss = s.escortOf !== null ? sim.state.squads.get(s.escortOf) : undefined;
      return boss ? boss.pos : s.pos;
    }
    default:
      return null;
  }
}

function attackRangeOf(s: Squad): number {
  return unitDef(s.defId).attack?.range ?? 1.5;
}

function clearTarget(s: Squad): void {
  s.targetId = null;
  s.engaged = false;
  s.retargetTimer = Math.max(s.retargetTimer, TUNING.retargetIntervalSeconds);
  if (s.command.kind === 'attack' && s.command.targetEntity === undefined) {
    s.command.pointA = undefined;
  }
}

/**
 * Should the squad keep closing on its target? Uses the same target-surface distance
 * combat fires at with hysteresis: approach until clearly inside, then hold position
 * until pushed clearly out. Without the dead band, squads at the exact range boundary
 * flip between move and stop every tick.
 */
function wantsToClose(sim: Sim, s: Squad, targetId: number, tp: Vec2): boolean {
  const stop = attackRangeOf(s); // combat allows +0.3 beyond this
  const d = distanceToTarget(sim, s.pos, targetId) ?? dist(s.pos, tp);
  if (s.mountedOnWall === null) {
    const building = sim.state.buildings.get(targetId);
    const sightPoint = building ? buildingAccessPoint(s.pos, building) : tp;
    if (!physicalLineClear(sim, s.pos, sightPoint, s.owner)) {
      // Planning may deliberately route through a friendly auto-gate while it is shut, but
      // a ground ranged squad cannot treat that planning corridor as physical line of fire.
      s.engaged = false;
      return true;
    }
  }
  if (s.engaged) {
    if (d <= stop + 0.25) return false; // hold: separation shoves smaller than this don't retrigger
    s.engaged = false;
    return true;
  }
  if (d > Math.max(0.6, stop - 0.35)) return true; // overshoot slightly inside the boundary
  s.engaged = true;
  return false;
}

/**
 * Chase point for an engaged squad. Buildings use a walkable stand-off point on their
 * near footprint face. Melee squads approach a slot around mobile targets instead of
 * stacking into one blob; ranged squads head straight in.
 */
function chasePoint(sim: Sim, s: Squad, targetId: number, tp: Vec2): Vec2 {
  const range = attackRangeOf(s);
  const building = sim.state.buildings.get(targetId);
  if (building) return buildingApproachPoint(s.pos, building, range);
  if (range > 4) return tp; // ranged: straight approach, combat stops them at range
  const d = dist(s.pos, tp);
  if (d < 4) return tp; // already closing — go straight in
  const angle = ((s.id % 12) / 12) * Math.PI * 2;
  const ring = Math.min(1.4, range * 0.8);
  return vec(tp.x + Math.cos(angle) * ring, tp.y + Math.sin(angle) * ring);
}

/** nearest safe location for Withdraw (A-25) */
function withdrawDestination(sim: Sim, s: Squad): Vec2 {
  const candidates: Vec2[] = [];
  const core = sim.coreAlive(s.owner);
  if (core) candidates.push(core.pos);
  for (const site of sim.state.sites) if (site.owner === s.owner) candidates.push(site.pos);
  if (candidates.length === 0) return core ? core.pos : s.pos;
  let bestSafe: Vec2 | null = null;
  let bestSafeD = Infinity;
  let bestFallback = candidates[0];
  let bestFallbackD = Infinity;
  const lowHealth = s.health / s.maxHealth < 0.25;
  for (const c of candidates) {
    let d = dist(s.pos, c);
    if (core && c === core.pos && lowHealth) d *= 0.7; // bias toward Core when hurt (A-25)
    if (d < bestFallbackD) {
      bestFallbackD = d;
      bestFallback = c;
    }
    let threatened = false;
    for (const enemy of sim.state.squads.values()) {
      if (sim.areHostile(s.owner, enemy.owner) && dist(enemy.pos, c) < TUNING.defendRadius + 6) {
        threatened = true;
        break;
      }
    }
    if (!threatened && d < bestSafeD) {
      bestSafeD = d;
      bestSafe = c;
    }
  }
  return bestSafe ?? bestFallback;
}

/** Escort role ring offset (A-24, simplified: role → radius/arc). */
function escortSlot(sim: Sim, s: Squad, bossPos: Vec2, bossFacing: number, index: number, count: number): Vec2 {
  const d = unitDef(s.defId);
  const isRanged = (d.attack?.range ?? 0) > 4;
  const isSiege = d.roleTags.includes('siege');
  const radius = isSiege ? 9 : isRanged ? 6 : 4;
  // melee front arc, ranged/siege rear arc
  const base = isRanged || isSiege ? bossFacing + Math.PI : bossFacing;
  const spread = Math.PI; // 180° arc
  const t = count > 1 ? index / (count - 1) - 0.5 : 0;
  const angle = base + t * spread;
  return vec(bossPos.x + Math.cos(angle) * radius, bossPos.y + Math.sin(angle) * radius);
}

export function stepCommands(sim: Sim): void {
  const st = sim.state;

  // pre-index escorts per boss for slot assignment
  const escortsByBoss = new Map<number, Squad[]>();
  for (const s of st.squads.values()) {
    if (s.escortOf !== null) {
      const arr = escortsByBoss.get(s.escortOf) ?? [];
      arr.push(s);
      escortsByBoss.set(s.escortOf, arr);
    }
  }

  for (const s of st.squads.values()) {
    const suspended = s.breachResume;
    if (suspended && sim.nav.canReach(s.pos, suspended.goal, s.owner)) {
      // The temporary blocker is gone/open. Restore the exact capture/defend/patrol/etc.
      // order instead of leaving every recovered squad in permanent attack-move mode.
      s.command = suspended.command;
      s.breachResume = null;
      clearTarget(s);
      s.path = null;
      s.pathGoal = null;
      s.repathCooldown = 0;
      s.stuckTime = 0;
      s.progressGoal = null;
      s.bestRouteRemaining = Infinity;
      s.progressNavRevision = sim.nav.revision;
      s.progressRouteSignature = 0;
    }
    const cmd = s.command;
    s.moveTarget = null;

    // garrisoned squads hold the battlements and just shoot (combat handles targeting)
    if (s.mountedOnWall !== null) {
      if (s.targetId !== null) {
        const tp = targetPosition(sim, s.targetId);
        if (tp) s.facing = Math.atan2(tp.y - s.pos.y, tp.x - s.pos.x);
      }
      continue;
    }

    // Auto-acquired targets are only valid while the squad can still reach their nav
    // region. This shared guard covers idle/defend/capture/patrol/raid/escort as well as
    // attack-move when a target retreats through a hostile gate after acquisition.
    const directPin = cmd.kind === 'attack' && cmd.targetEntity !== undefined;
    if (s.targetId !== null && !directPin) {
      const tp = targetPosition(sim, s.targetId);
      const targetBuilding = sim.state.buildings.get(s.targetId);
      const accessPoint = tp
        ? (targetBuilding ? buildingAccessPoint(s.pos, targetBuilding) : tp)
        : null;
      if (
        !accessPoint ||
        !isHostileTarget(sim, s.owner, s.targetId) ||
        !sim.nav.canReach(s.pos, accessPoint, s.owner)
      ) clearTarget(s);
    }

    switch (cmd.kind) {
      case 'idle': {
        // hold near anchor; chase handled via combat within leash 8
        const anchor = cmd.pointA ?? s.pos;
        if (s.targetId !== null) {
          const tp = targetPosition(sim, s.targetId);
          const targetD = tp ? (distanceToTarget(sim, anchor, s.targetId) ?? dist(anchor, tp)) : Infinity;
          if (tp && targetD < 8 + attackRangeOf(s)) {
            if (wantsToClose(sim, s, s.targetId, tp)) {
              s.moveTarget = chasePoint(sim, s, s.targetId, tp);
            }
          } else {
            clearTarget(s);
          }
        } else if (dist(s.pos, anchor) > 3) {
          s.moveTarget = anchor;
        }
        break;
      }
      case 'move': {
        // A ground move is a relocation order, not attack-move. Do not let a target
        // acquired on the previous tick root or turn the squad while it disengages.
        clearTarget(s);
        if (!cmd.targetPos) {
          s.command = { kind: 'idle', pointA: { ...s.pos } };
          break;
        }
        s.moveTarget = cmd.targetPos;
        if (dist(s.pos, cmd.targetPos) < 1.5) {
          s.command = { kind: 'idle', pointA: { ...s.pos } };
        }
        break;
      }
      case 'attack': {
        if (cmd.targetEntity !== undefined) {
          const tp = targetPosition(sim, cmd.targetEntity);
          if (!tp || !isHostileTarget(sim, s.owner, cmd.targetEntity)) {
            clearTarget(s);
            // target destroyed → idle-defend in place (§19.2)
            s.command = { kind: 'idle', pointA: { ...s.pos } };
            break;
          }
          // pursuit anchor: the order's click point (AI orders without one anchor at the
          // target's position when the chase starts)
          if (!cmd.targetPos) cmd.targetPos = { ...tp };
          if (dist(tp, cmd.targetPos) > TUNING.attackChaseLeash) {
            // target fled beyond the leash — give up the pin, attack-move at the anchor
            clearTarget(s);
            s.command = { kind: 'attack', targetPos: cmd.targetPos };
            break;
          }
          const targetBuilding = sim.state.buildings.get(cmd.targetEntity);
          const accessPoint = targetBuilding ? buildingAccessPoint(s.pos, targetBuilding) : tp;
          if (!sim.nav.canReach(s.pos, accessPoint, s.owner)) {
            // A mobile target can retreat through a gate after the order was issued. Drop
            // the stale pin and attack-move at its current region so blockers are engaged.
            const fallback = sim.nav.nearestWalkable(accessPoint, 12, s.owner, s.pos);
            clearTarget(s);
            s.command = { kind: 'attack', targetPos: fallback };
            s.moveTarget = fallback;
            break;
          }
          s.targetId = cmd.targetEntity; // pinned focus (§17.3)
          if (wantsToClose(sim, s, s.targetId, tp)) {
            s.moveTarget = chasePoint(sim, s, s.targetId, tp);
          }
        } else if (cmd.targetPos) {
          // attack-move: engage anything en route (combat acquires); advance otherwise
          if (s.targetId !== null) {
            const tp = targetPosition(sim, s.targetId);
            const chaseLimit = Math.max(12, attackRangeOf(s) + TUNING.acquisitionExtraRange + 3);
            const pursuitOrigin = cmd.pointA ?? s.pos;
            const targetD = tp
              ? (distanceToTarget(sim, pursuitOrigin, s.targetId) ?? dist(pursuitOrigin, tp))
              : Infinity;
            if (
              !tp ||
              !isHostileTarget(sim, s.owner, s.targetId) ||
              targetD > chaseLimit
            ) {
              clearTarget(s);
              // Give the squad time to resume its route before scanning around its new
              // position; otherwise the same kiting target is reacquired in this tick.
              s.retargetTimer = Math.max(s.retargetTimer, TUNING.retargetIntervalSeconds * 5);
            } else if (wantsToClose(sim, s, s.targetId, tp)) {
              s.moveTarget = chasePoint(sim, s, s.targetId, tp);
            }
          }
          if (s.targetId === null) {
            cmd.pointA = undefined;
            s.moveTarget = cmd.targetPos;
            if (dist(s.pos, cmd.targetPos) < 2) s.command = { kind: 'idle', pointA: { ...s.pos } };
          }
        }
        break;
      }
      case 'capture': {
        const site = st.sites.find((x) => x.id === cmd.siteId);
        if (!site) {
          s.command = { kind: 'idle', pointA: { ...s.pos } };
          break;
        }
        const d = dist(s.pos, site.pos);
        if (s.targetId !== null) {
          // fight blockers inside the radius; do not chase out (§19.2)
          const tp = targetPosition(sim, s.targetId);
          const targetD = tp ? (distanceToTarget(sim, site.pos, s.targetId) ?? dist(tp, site.pos)) : Infinity;
          if (!tp || targetD > TUNING.captureRadius + 3) clearTarget(s);
          else if (wantsToClose(sim, s, s.targetId, tp)) {
            s.moveTarget = chasePoint(sim, s, s.targetId, tp);
          }
        } else if (d > TUNING.captureRadius - 2) {
          s.moveTarget = site.pos;
        }
        break;
      }
      case 'defend': {
        const center = cmd.pointA ?? s.pos;
        const ring = cmd.siteId ? TUNING.defendRadius * 0.55 : 6;
        if (s.targetId !== null) {
          const tp = targetPosition(sim, s.targetId);
          const leash = (cmd.siteId ? TUNING.defendRadius : 10) + 8;
          const targetD = tp ? (distanceToTarget(sim, center, s.targetId) ?? dist(tp, center)) : Infinity;
          if (!tp || targetD > leash) clearTarget(s);
          else if (wantsToClose(sim, s, s.targetId, tp)) {
            s.moveTarget = chasePoint(sim, s, s.targetId, tp);
          }
        } else {
          // fixed guard post on the ring, staggered by squad id. (A drifting slot made
          // squads hover at the move/stop threshold and twitch — glitchy walk anim.)
          const ang = ((s.id % 8) / 8) * Math.PI * 2;
          const slot = vec(center.x + Math.cos(ang) * ring, center.y + Math.sin(ang) * ring);
          if (dist(s.pos, slot) > 2.5) s.moveTarget = slot;
        }
        break;
      }
      case 'patrol': {
        const goal = cmd.patrolLegToB ? cmd.pointB : cmd.pointA;
        if (!goal || !cmd.pointA || !cmd.pointB) {
          s.command = { kind: 'idle', pointA: { ...s.pos } };
          break;
        }
        if (s.targetId !== null) {
          const tp = targetPosition(sim, s.targetId);
          const targetD = tp ? (distanceToTarget(sim, s.pos, s.targetId) ?? dist(tp, s.pos)) : Infinity;
          if (!tp || targetD > 12) clearTarget(s);
          else if (wantsToClose(sim, s, s.targetId, tp)) {
            s.moveTarget = chasePoint(sim, s, s.targetId, tp);
          }
        } else {
          s.moveTarget = goal;
          if (dist(s.pos, goal) < 2) cmd.patrolLegToB = !cmd.patrolLegToB;
        }
        break;
      }
      case 'raid': {
        // combat biases acquisition toward economy targets; move toward raid point
        if (s.targetId !== null) {
          const tp = targetPosition(sim, s.targetId);
          if (tp) {
            const anchor = cmd.targetPos ?? s.pos;
            const targetD = distanceToTarget(sim, anchor, s.targetId) ?? dist(tp, anchor);
            if (targetD <= chaseLeash(s)) {
              if (wantsToClose(sim, s, s.targetId, tp)) {
                s.moveTarget = chasePoint(sim, s, s.targetId, tp);
              }
              break;
            }
          }
          clearTarget(s);
        }
        if (cmd.targetPos) {
          s.moveTarget = cmd.targetPos;
          if (dist(s.pos, cmd.targetPos) < 3) {
            // arrived; hold as raider (keeps hunting via combat bias)
            s.command = { kind: 'idle', pointA: { ...s.pos } };
          }
        }
        break;
      }
      case 'escort': {
        const boss = s.escortOf !== null ? st.squads.get(s.escortOf) : undefined;
        if (!boss) {
          s.escortOf = null;
          s.command = { kind: 'defend', pointA: { ...s.pos }, auto: true };
          break;
        }
        const escorts = escortsByBoss.get(boss.id) ?? [];
        const index = escorts.indexOf(s);
        const slot = escortSlot(sim, s, boss.pos, boss.facing, Math.max(0, index), Math.max(1, escorts.length));
        s.escortSlot = slot;
        if (s.targetId !== null) {
          const tp = targetPosition(sim, s.targetId);
          const targetD = tp ? (distanceToTarget(sim, boss.pos, s.targetId) ?? dist(tp, boss.pos)) : Infinity;
          if (!tp || targetD > 14) clearTarget(s); // engage only threats near boss
          else if (wantsToClose(sim, s, s.targetId, tp)) {
            s.moveTarget = chasePoint(sim, s, s.targetId, tp);
          }
        }
        if (s.moveTarget === null && dist(s.pos, slot) > 2) s.moveTarget = slot;
        break;
      }
      case 'withdraw': {
        clearTarget(s); // no attacks, retaliation OFF (§19.2)
        const dest = withdrawDestination(sim, s);
        s.moveTarget = dest;
        if (dist(s.pos, dest) < 4) {
          s.command = { kind: 'defend', pointA: { ...s.pos }, auto: true };
        }
        break;
      }
    }

    // face movement direction or target
    if (s.moveTarget) {
      const dir = sub(s.moveTarget, s.pos);
      if (dir.x !== 0 || dir.y !== 0) {
        const n = norm(dir);
        s.facing = Math.atan2(n.y, n.x);
      }
    }
  }
}
